/**
 * Every translatable string of the public API guide (`/$lang/api`).
 *
 * Split out of the route so the page stays readable and so a translator can work on one
 * file. The eight locales are complete: the guide is a sales page as much as a
 * reference, and half of it in English reads as "this product is not for you".
 *
 * What is deliberately NOT translated:
 *   - code samples, header names, field names and error `code` values. They are
 *     identifiers a client compares against, and a translated identifier is a bug.
 *     Only the "what to do about it" column of the error table is prose.
 */

export type ApiDocsContent = {
  metaTitle: string;
  metaDescription: string;
  heading: string;
  subheading: string;
  bullets: [string, string, string];
  ctaKey: string;
  ctaReference: string;

  quickstartTitle: string;
  quickstartBody: string;

  conventionsTitle: string;
  /** Rendered as a bulleted list; `<0>…</0>` marks the inline <code> span. */
  conventions: [string, string, string, string, string];

  idempotencyTitle: string;
  idempotencyBody: [string, string, string];

  maxPriceTitle: string;
  maxPriceBody: string;

  webhooksTitle: string;
  webhooksBody: [string, string];

  verifyTitle: string;
  verifyIntro: string;
  verifyMistakes: [string, string, string, string];
  verifyRotation: string;

  errorsTitle: string;
  errorsColCode: string;
  errorsColWhat: string;
  /** Keyed by the untranslated error code. */
  errors: Record<string, string>;

  lifecycleTitle: string;
  lifecycleBody: [string, string, string];

  limitsTitle: string;
  limitsBody: string;

  referenceTitle: string;
  referenceBody: string;
  referenceCta: string;
};

export const ERROR_CODES = [
  "INVALID_API_KEY",
  "KEY_REVOKED / KEY_EXPIRED",
  "INSUFFICIENT_SCOPE",
  "API_ACCESS_DENIED",
  "API_DISABLED",
  "IDEMPOTENCY_KEY_REQUIRED",
  "IDEMPOTENCY_KEY_REUSED",
  "IDEMPOTENCY_IN_PROGRESS",
  "PRICE_ABOVE_MAX",
  "INSUFFICIENT_BALANCE",
  "COUNTRY_OUT_OF_STOCK",
  "PROVIDER_CAPACITY",
  "CONCURRENCY_LIMIT",
  "SPEND_LIMIT",
  "RATE_LIMITED",
  "VALIDATION_ERROR",
] as const;

const en: ApiDocsContent = {
  metaTitle: "SMS & Email verification API — SmsEmpire",
  metaDescription:
    "Buy SMS and email verifications from your own code. Signed webhooks, idempotent purchases and a discount off the website price.",
  heading: "The SmsEmpire API",
  subheading:
    "Buy SMS and email verifications from your own code, paid from your balance, at a discount off the website price.",
  bullets: [
    "Discounted pricing on every purchase made through the API",
    "Signed webhooks the moment a code arrives — no polling loop needed",
    "Idempotent purchases, so a network timeout is safe to retry",
  ],
  ctaKey: "Get your API key",
  ctaReference: "OpenAPI reference",

  quickstartTitle: "Quickstart",
  quickstartBody:
    "Everything lives under https://smsempire.com/api/v1. Authenticate with your key as a bearer token. Server-side only — this API sends no CORS headers, and a key used from a browser is a key that has leaked.",

  conventionsTitle: "Conventions",
  conventions: [
    'Money is a decimal string ("0.28000"), never a JSON number. Parse it with a decimal type, not a float.',
    "Timestamps are RFC3339 UTC.",
    'Errors are always {"error": {"code", "message", "request_id"}}. Branch on code; message may change.',
    "Quote request_id in any support ticket — it points straight at the request in our logs.",
    "Rate limits come back in X-RateLimit-* headers, and a 429 carries Retry-After.",
  ],

  idempotencyTitle: "Idempotency (required on purchases)",
  idempotencyBody: [
    "Every purchase needs an Idempotency-Key header — a UUID is ideal. Reusing it returns the original response with Idempotent-Replay: true instead of buying a second number.",
    "This exists for one situation: your POST times out and you do not know whether it went through. Without the key, retrying buys twice and not retrying loses the number you already paid for. With it, just retry.",
    "Reusing a key with a different body is a 422, not a silent replay — that combination is a bug on your side, and hiding it would give you the wrong result.",
  ],

  maxPriceTitle: "max_price",
  maxPriceBody:
    "Optional but strongly recommended. If you omit the country we try several, in quality order, at different prices — max_price is checked against every candidate, so you can never be charged more than you agreed to. If nothing fits, you get 409 PRICE_ABOVE_MAX with the current price and nothing is charged.",

  webhooksTitle: "Webhooks",
  webhooksBody: [
    "Register one HTTPS endpoint in your dashboard. We POST an event as soon as a code arrives. Delivery is at least once, retried with backoff over roughly two and a half hours.",
    "Requirements for your endpoint: HTTPS on port 443, resolving to a public address. We do not follow redirects and we refuse private, loopback and cloud-metadata destinations.",
  ],

  verifyTitle: "Verifying a webhook",
  verifyIntro:
    'The signature is HMAC-SHA256(secret, "<timestamp>.<raw body>"), hex-encoded. Four things people get wrong, in order of how often:',
  verifyMistakes: [
    "Signing the re-serialised JSON instead of the raw bytes. Re-serialising changes the bytes and the signature will never match.",
    "Ignoring the timestamp. Without the ±300s check, a captured delivery can be replayed at you forever.",
    "Comparing with == instead of a constant-time compare.",
    "Not deduplicating on X-SmsEmpire-Delivery. We guarantee at least once, not exactly once.",
  ],
  verifyRotation:
    "Rotating the secret keeps the previous one valid for 24 hours, and both sign every delivery during that window — so you can deploy without downtime.",

  errorsTitle: "Errors",
  errorsColCode: "code",
  errorsColWhat: "What to do",
  errors: {
    INVALID_API_KEY: "Missing, malformed or unknown key. Do not retry.",
    "KEY_REVOKED / KEY_EXPIRED": "Create a new key. Do not retry.",
    INSUFFICIENT_SCOPE: "This key was created without write access.",
    API_ACCESS_DENIED: "API access is off for this account, or you have not topped up yet.",
    API_DISABLED: "The API is temporarily disabled platform-wide. Retry later.",
    IDEMPOTENCY_KEY_REQUIRED: "Send an Idempotency-Key header on every purchase.",
    IDEMPOTENCY_KEY_REUSED: "Same key, different body. Fix your client.",
    IDEMPOTENCY_IN_PROGRESS: "The first attempt is still running. Retry in a second.",
    PRICE_ABOVE_MAX: "The price moved above your max_price. Re-quote and decide.",
    INSUFFICIENT_BALANCE: "Top up. Do not retry until you have.",
    COUNTRY_OUT_OF_STOCK: "No numbers right now. Retry, or try another country.",
    PROVIDER_CAPACITY: "Temporarily unable to source that country. Retry later.",
    CONCURRENCY_LIMIT: "Too many pending activations. Finish or cancel some.",
    SPEND_LIMIT: "Hourly spend ceiling reached. Retry after the window.",
    RATE_LIMITED: "Slow down. Honour Retry-After.",
    VALIDATION_ERROR: "Your request body is wrong. Do not retry unchanged.",
  },

  lifecycleTitle: "Activation lifecycle",
  lifecycleBody: [
    "An activation is pending until a code arrives (received), you mark it used (finished), or it is cancelled. Cancelling refunds exactly what you were charged.",
    "A pending activation expires 20 minutes after purchase. We cancel it with the network and refund you in full once that cancellation is confirmed — you are never left paying for a number that never received anything.",
    "There is a ~120 second grace period before a number can be cancelled; cancelling sooner returns EARLY_CANCEL_DENIED with the number of seconds to wait.",
  ],

  limitsTitle: "Limits",
  limitsBody:
    "Read requests, write requests and total per-user requests each have a per-minute ceiling, and there are optional caps on pending activations, hourly spend and hourly activation count. Your current values are in GET /api/v1/me, where null means no limit. If you need them raised, open a ticket — they are per-account settings, not hard-coded.",

  referenceTitle: "Full endpoint reference",
  referenceBody: "Interactive OpenAPI 3.1 document, always generated from the running code.",
  referenceCta: "Open the reference",
};

const es: ApiDocsContent = {
  metaTitle: "API de verificación SMS y email — SmsEmpire",
  metaDescription:
    "Compra verificaciones SMS y email desde tu propio código. Webhooks firmados, compras idempotentes y descuento sobre el precio de la web.",
  heading: "La API de SmsEmpire",
  subheading:
    "Compra verificaciones SMS y email desde tu propio código, con cargo a tu saldo y con descuento sobre el precio de la web.",
  bullets: [
    "Precio con descuento en cada compra hecha por API",
    "Webhooks firmados en cuanto llega el código — sin bucle de sondeo",
    "Compras idempotentes: reintentar tras un timeout es seguro",
  ],
  ctaKey: "Consigue tu clave de API",
  ctaReference: "Referencia OpenAPI",

  quickstartTitle: "Inicio rápido",
  quickstartBody:
    "Todo cuelga de https://smsempire.com/api/v1. Autentícate enviando tu clave como bearer token. Solo desde servidor: esta API no envía cabeceras CORS, y una clave usada desde un navegador es una clave filtrada.",

  conventionsTitle: "Convenciones",
  conventions: [
    'El dinero es una cadena decimal ("0.28000"), nunca un número JSON. Parséalo con un tipo decimal, no con un float.',
    "Las fechas son RFC3339 en UTC.",
    'Los errores siempre son {"error": {"code", "message", "request_id"}}. Decide según code; message puede cambiar.',
    "Cita el request_id en cualquier ticket de soporte: apunta directamente a esa petición en nuestros registros.",
    "Los límites vienen en las cabeceras X-RateLimit-*, y un 429 incluye Retry-After.",
  ],

  idempotencyTitle: "Idempotencia (obligatoria al comprar)",
  idempotencyBody: [
    "Toda compra necesita una cabecera Idempotency-Key — lo ideal es un UUID. Reutilizarla devuelve la respuesta original con Idempotent-Replay: true en vez de comprar un segundo número.",
    "Existe por una sola situación: tu POST da timeout y no sabes si llegó a ejecutarse. Sin la clave, reintentar compra dos veces y no reintentar pierde el número que ya pagaste. Con ella, simplemente reintenta.",
    "Reutilizar una clave con un cuerpo distinto devuelve 422, no una repetición silenciosa: esa combinación es un fallo de tu cliente, y taparlo te daría un resultado equivocado.",
  ],

  maxPriceTitle: "max_price",
  maxPriceBody:
    "Opcional, pero muy recomendable. Si no indicas país probamos varios, por orden de calidad y a precios distintos; max_price se comprueba contra cada candidato, así que nunca se te puede cobrar más de lo que aceptaste. Si ninguno encaja recibes un 409 PRICE_ABOVE_MAX con el precio actual, y no se cobra nada.",

  webhooksTitle: "Webhooks",
  webhooksBody: [
    "Registra un endpoint HTTPS en tu panel. Enviamos un POST con el evento en cuanto llega el código. La entrega es al menos una vez, con reintentos escalonados durante unas dos horas y media.",
    "Requisitos de tu endpoint: HTTPS en el puerto 443 y que resuelva a una dirección pública. No seguimos redirecciones y rechazamos destinos privados, de loopback o de metadatos de nube.",
  ],

  verifyTitle: "Verificar un webhook",
  verifyIntro:
    'La firma es HMAC-SHA256(secreto, "<timestamp>.<cuerpo crudo>") en hexadecimal. Cuatro cosas que se hacen mal, por orden de frecuencia:',
  verifyMistakes: [
    "Firmar el JSON reserializado en vez de los bytes crudos. Reserializar cambia los bytes y la firma nunca cuadrará.",
    "Ignorar el timestamp. Sin la comprobación de ±300 s, una entrega capturada se te puede reenviar para siempre.",
    "Comparar con == en lugar de con una comparación en tiempo constante.",
    "No deduplicar por X-SmsEmpire-Delivery. Garantizamos al menos una vez, no exactamente una.",
  ],
  verifyRotation:
    "Al rotar el secreto, el anterior sigue siendo válido 24 horas y ambos firman cada entrega durante esa ventana, así que puedes desplegar sin cortes.",

  errorsTitle: "Errores",
  errorsColCode: "code",
  errorsColWhat: "Qué hacer",
  errors: {
    INVALID_API_KEY: "Clave ausente, mal formada o desconocida. No reintentes.",
    "KEY_REVOKED / KEY_EXPIRED": "Crea una clave nueva. No reintentes.",
    INSUFFICIENT_SCOPE: "Esta clave se creó sin permiso de escritura.",
    API_ACCESS_DENIED: "La API está desactivada para esta cuenta, o aún no has recargado.",
    API_DISABLED: "La API está desactivada temporalmente para todos. Reintenta más tarde.",
    IDEMPOTENCY_KEY_REQUIRED: "Envía la cabecera Idempotency-Key en cada compra.",
    IDEMPOTENCY_KEY_REUSED: "Misma clave, cuerpo distinto. Corrige tu cliente.",
    IDEMPOTENCY_IN_PROGRESS: "El primer intento sigue en curso. Reintenta en un segundo.",
    PRICE_ABOVE_MAX: "El precio subió por encima de tu max_price. Vuelve a consultar y decide.",
    INSUFFICIENT_BALANCE: "Recarga saldo. No reintentes hasta hacerlo.",
    COUNTRY_OUT_OF_STOCK: "Ahora mismo no hay números. Reintenta o prueba otro país.",
    PROVIDER_CAPACITY: "No podemos servir ese país temporalmente. Reintenta más tarde.",
    CONCURRENCY_LIMIT: "Demasiadas activaciones pendientes. Finaliza o cancela algunas.",
    SPEND_LIMIT: "Alcanzado el techo de gasto por hora. Reintenta pasada la ventana.",
    RATE_LIMITED: "Baja el ritmo. Respeta Retry-After.",
    VALIDATION_ERROR: "El cuerpo de tu petición es incorrecto. No reintentes sin cambiarlo.",
  },

  lifecycleTitle: "Ciclo de vida de una activación",
  lifecycleBody: [
    "Una activación está pending hasta que llega el código (received), la marcas como usada (finished) o se cancela. Cancelar devuelve exactamente lo que se te cobró.",
    "Una activación pendiente caduca a los 20 minutos de la compra. La cancelamos en la red y te devolvemos el importe íntegro en cuanto esa cancelación queda confirmada: nunca te quedas pagando un número que no recibió nada.",
    "Hay un periodo de gracia de unos 120 segundos antes de poder cancelar un número; cancelar antes devuelve EARLY_CANCEL_DENIED con los segundos que faltan.",
  ],

  limitsTitle: "Límites",
  limitsBody:
    "Las peticiones de lectura, las de escritura y el total por usuario tienen cada una un techo por minuto, y existen topes opcionales de activaciones pendientes, gasto por hora y activaciones por hora. Tus valores actuales están en GET /api/v1/me, donde null significa sin límite. Si necesitas ampliarlos, abre un ticket: son ajustes por cuenta, no valores fijos en el código.",

  referenceTitle: "Referencia completa de endpoints",
  referenceBody:
    "Documento OpenAPI 3.1 interactivo, generado siempre desde el código en ejecución.",
  referenceCta: "Abrir la referencia",
};

const fr: ApiDocsContent = {
  metaTitle: "API de vérification SMS et e-mail — SmsEmpire",
  metaDescription:
    "Achetez des vérifications SMS et e-mail depuis votre code. Webhooks signés, achats idempotents et remise sur le prix du site.",
  heading: "L'API SmsEmpire",
  subheading:
    "Achetez des vérifications SMS et e-mail depuis votre propre code, débitées de votre solde, avec une remise sur le prix du site.",
  bullets: [
    "Tarif remisé sur chaque achat effectué via l'API",
    "Webhooks signés dès l'arrivée du code — pas de boucle d'interrogation",
    "Achats idempotents : réessayer après un timeout est sans risque",
  ],
  ctaKey: "Obtenir votre clé d'API",
  ctaReference: "Référence OpenAPI",

  quickstartTitle: "Démarrage rapide",
  quickstartBody:
    "Tout se trouve sous https://smsempire.com/api/v1. Authentifiez-vous en envoyant votre clé comme bearer token. Côté serveur uniquement : cette API n'envoie aucun en-tête CORS, et une clé utilisée depuis un navigateur est une clé divulguée.",

  conventionsTitle: "Conventions",
  conventions: [
    'L\'argent est une chaîne décimale ("0.28000"), jamais un nombre JSON. Analysez-la avec un type décimal, pas un float.',
    "Les horodatages sont en RFC3339 UTC.",
    'Les erreurs sont toujours {"error": {"code", "message", "request_id"}}. Branchez sur code ; message peut changer.',
    "Citez le request_id dans tout ticket de support : il pointe directement sur la requête dans nos journaux.",
    "Les limites reviennent dans les en-têtes X-RateLimit-*, et un 429 porte Retry-After.",
  ],

  idempotencyTitle: "Idempotence (obligatoire à l'achat)",
  idempotencyBody: [
    "Chaque achat exige un en-tête Idempotency-Key — un UUID est idéal. La réutiliser renvoie la réponse d'origine avec Idempotent-Replay: true au lieu d'acheter un second numéro.",
    "Cela existe pour une seule situation : votre POST expire et vous ignorez s'il a abouti. Sans la clé, réessayer achète deux fois et ne pas réessayer perd le numéro déjà payé. Avec elle, réessayez simplement.",
    "Réutiliser une clé avec un corps différent renvoie 422, pas une répétition silencieuse : cette combinaison est un bug de votre côté, et la masquer vous donnerait un résultat faux.",
  ],

  maxPriceTitle: "max_price",
  maxPriceBody:
    "Facultatif mais vivement recommandé. Si vous omettez le pays, nous en essayons plusieurs, par ordre de qualité et à des prix différents ; max_price est vérifié sur chaque candidat, vous ne pouvez donc jamais être facturé plus que ce que vous avez accepté. Si rien ne convient, vous obtenez 409 PRICE_ABOVE_MAX avec le prix actuel, et rien n'est débité.",

  webhooksTitle: "Webhooks",
  webhooksBody: [
    "Enregistrez un endpoint HTTPS dans votre tableau de bord. Nous envoyons un POST dès qu'un code arrive. La livraison est au moins une fois, réessayée avec un délai croissant sur environ deux heures et demie.",
    "Exigences pour votre endpoint : HTTPS sur le port 443, résolvant vers une adresse publique. Nous ne suivons pas les redirections et refusons les destinations privées, loopback et métadonnées cloud.",
  ],

  verifyTitle: "Vérifier un webhook",
  verifyIntro:
    'La signature est HMAC-SHA256(secret, "<timestamp>.<corps brut>"), encodée en hexadécimal. Quatre erreurs courantes, par ordre de fréquence :',
  verifyMistakes: [
    "Signer le JSON re-sérialisé au lieu des octets bruts. La re-sérialisation change les octets et la signature ne correspondra jamais.",
    "Ignorer l'horodatage. Sans la vérification ±300 s, une livraison capturée peut vous être rejouée indéfiniment.",
    "Comparer avec == au lieu d'une comparaison à temps constant.",
    "Ne pas dédupliquer sur X-SmsEmpire-Delivery. Nous garantissons au moins une fois, pas exactement une fois.",
  ],
  verifyRotation:
    "La rotation du secret conserve le précédent valide 24 heures, et les deux signent chaque livraison pendant cette fenêtre — vous pouvez donc déployer sans interruption.",

  errorsTitle: "Erreurs",
  errorsColCode: "code",
  errorsColWhat: "Que faire",
  errors: {
    INVALID_API_KEY: "Clé absente, mal formée ou inconnue. Ne réessayez pas.",
    "KEY_REVOKED / KEY_EXPIRED": "Créez une nouvelle clé. Ne réessayez pas.",
    INSUFFICIENT_SCOPE: "Cette clé a été créée sans accès en écriture.",
    API_ACCESS_DENIED: "L'API est désactivée pour ce compte, ou vous n'avez pas encore rechargé.",
    API_DISABLED: "L'API est temporairement désactivée pour tous. Réessayez plus tard.",
    IDEMPOTENCY_KEY_REQUIRED: "Envoyez un en-tête Idempotency-Key à chaque achat.",
    IDEMPOTENCY_KEY_REUSED: "Même clé, corps différent. Corrigez votre client.",
    IDEMPOTENCY_IN_PROGRESS:
      "La première tentative est encore en cours. Réessayez dans une seconde.",
    PRICE_ABOVE_MAX: "Le prix a dépassé votre max_price. Redemandez un devis et décidez.",
    INSUFFICIENT_BALANCE: "Rechargez. Ne réessayez pas avant.",
    COUNTRY_OUT_OF_STOCK: "Aucun numéro pour l'instant. Réessayez ou choisissez un autre pays.",
    PROVIDER_CAPACITY: "Impossible de fournir ce pays temporairement. Réessayez plus tard.",
    CONCURRENCY_LIMIT: "Trop d'activations en attente. Terminez-en ou annulez-en.",
    SPEND_LIMIT: "Plafond de dépense horaire atteint. Réessayez après la fenêtre.",
    RATE_LIMITED: "Ralentissez. Respectez Retry-After.",
    VALIDATION_ERROR: "Le corps de votre requête est incorrect. Ne réessayez pas sans le modifier.",
  },

  lifecycleTitle: "Cycle de vie d'une activation",
  lifecycleBody: [
    "Une activation est pending jusqu'à l'arrivée d'un code (received), jusqu'à ce que vous la marquiez utilisée (finished) ou qu'elle soit annulée. L'annulation rembourse exactement ce qui a été facturé.",
    "Une activation en attente expire 20 minutes après l'achat. Nous l'annulons auprès du réseau et vous remboursons intégralement une fois cette annulation confirmée — vous ne payez jamais pour un numéro qui n'a rien reçu.",
    "Un délai de grâce d'environ 120 secondes s'applique avant de pouvoir annuler un numéro ; annuler plus tôt renvoie EARLY_CANCEL_DENIED avec le nombre de secondes à attendre.",
  ],

  limitsTitle: "Limites",
  limitsBody:
    "Les requêtes en lecture, en écriture et le total par utilisateur ont chacun un plafond par minute, et des plafonds facultatifs existent sur les activations en attente, la dépense horaire et le nombre d'activations par heure. Vos valeurs actuelles sont dans GET /api/v1/me, où null signifie aucune limite. Pour les augmenter, ouvrez un ticket : ce sont des réglages par compte, pas des valeurs codées en dur.",

  referenceTitle: "Référence complète des endpoints",
  referenceBody:
    "Document OpenAPI 3.1 interactif, toujours généré à partir du code en cours d'exécution.",
  referenceCta: "Ouvrir la référence",
};

const de: ApiDocsContent = {
  metaTitle: "SMS- und E-Mail-Verifizierungs-API — SmsEmpire",
  metaDescription:
    "Kaufen Sie SMS- und E-Mail-Verifizierungen aus Ihrem eigenen Code. Signierte Webhooks, idempotente Käufe und Rabatt auf den Website-Preis.",
  heading: "Die SmsEmpire-API",
  subheading:
    "Kaufen Sie SMS- und E-Mail-Verifizierungen aus Ihrem eigenen Code, bezahlt aus Ihrem Guthaben, mit Rabatt auf den Website-Preis.",
  bullets: [
    "Rabattierter Preis bei jedem Kauf über die API",
    "Signierte Webhooks, sobald der Code eintrifft — keine Polling-Schleife",
    "Idempotente Käufe: ein Timeout lässt sich gefahrlos wiederholen",
  ],
  ctaKey: "API-Schlüssel holen",
  ctaReference: "OpenAPI-Referenz",

  quickstartTitle: "Schnellstart",
  quickstartBody:
    "Alles liegt unter https://smsempire.com/api/v1. Authentifizieren Sie sich mit Ihrem Schlüssel als Bearer-Token. Nur serverseitig — diese API sendet keine CORS-Header, und ein im Browser verwendeter Schlüssel ist ein geleakter Schlüssel.",

  conventionsTitle: "Konventionen",
  conventions: [
    'Geld ist ein Dezimal-String ("0.28000"), nie eine JSON-Zahl. Mit einem Decimal-Typ parsen, nicht als Float.',
    "Zeitstempel sind RFC3339 UTC.",
    'Fehler sind immer {"error": {"code", "message", "request_id"}}. Verzweigen Sie über code; message kann sich ändern.',
    "Nennen Sie die request_id in jedem Support-Ticket — sie zeigt direkt auf die Anfrage in unseren Logs.",
    "Limits kommen in den X-RateLimit-*-Headern zurück, und ein 429 trägt Retry-After.",
  ],

  idempotencyTitle: "Idempotenz (bei Käufen erforderlich)",
  idempotencyBody: [
    "Jeder Kauf braucht einen Idempotency-Key-Header — eine UUID ist ideal. Wiederverwendung liefert die ursprüngliche Antwort mit Idempotent-Replay: true, statt eine zweite Nummer zu kaufen.",
    "Das gibt es für genau eine Situation: Ihr POST läuft in einen Timeout und Sie wissen nicht, ob er durchging. Ohne den Schlüssel kauft ein Retry doppelt, und kein Retry verliert die bereits bezahlte Nummer. Mit ihm wiederholen Sie einfach.",
    "Derselbe Schlüssel mit anderem Body ergibt 422, keine stille Wiederholung — diese Kombination ist ein Fehler auf Ihrer Seite, und ihn zu verbergen würde Ihnen ein falsches Ergebnis liefern.",
  ],

  maxPriceTitle: "max_price",
  maxPriceBody:
    "Optional, aber dringend empfohlen. Ohne Länderangabe probieren wir mehrere, nach Qualität sortiert und zu unterschiedlichen Preisen; max_price wird gegen jeden Kandidaten geprüft, sodass Ihnen nie mehr berechnet werden kann als vereinbart. Passt keiner, erhalten Sie 409 PRICE_ABOVE_MAX mit dem aktuellen Preis, und es wird nichts belastet.",

  webhooksTitle: "Webhooks",
  webhooksBody: [
    "Registrieren Sie einen HTTPS-Endpunkt in Ihrem Dashboard. Wir senden ein POST, sobald ein Code eintrifft. Die Zustellung erfolgt mindestens einmal, mit Backoff über rund zweieinhalb Stunden wiederholt.",
    "Anforderungen an Ihren Endpunkt: HTTPS auf Port 443, auflösend auf eine öffentliche Adresse. Wir folgen keinen Weiterleitungen und lehnen private, Loopback- und Cloud-Metadaten-Ziele ab.",
  ],

  verifyTitle: "Einen Webhook verifizieren",
  verifyIntro:
    'Die Signatur ist HMAC-SHA256(secret, "<timestamp>.<Roh-Body>"), hex-kodiert. Vier häufige Fehler, nach Häufigkeit:',
  verifyMistakes: [
    "Das re-serialisierte JSON signieren statt der Roh-Bytes. Re-Serialisieren ändert die Bytes, und die Signatur passt nie.",
    "Den Zeitstempel ignorieren. Ohne die ±300-s-Prüfung lässt sich eine abgefangene Zustellung ewig gegen Sie wiederholen.",
    "Mit == vergleichen statt mit einem zeitkonstanten Vergleich.",
    "Nicht über X-SmsEmpire-Delivery deduplizieren. Wir garantieren mindestens einmal, nicht genau einmal.",
  ],
  verifyRotation:
    "Beim Rotieren bleibt das vorherige Secret 24 Stunden gültig, und beide signieren in diesem Fenster jede Zustellung — Sie können also ohne Ausfall deployen.",

  errorsTitle: "Fehler",
  errorsColCode: "code",
  errorsColWhat: "Was zu tun ist",
  errors: {
    INVALID_API_KEY: "Schlüssel fehlt, ist fehlerhaft oder unbekannt. Nicht wiederholen.",
    "KEY_REVOKED / KEY_EXPIRED": "Neuen Schlüssel erstellen. Nicht wiederholen.",
    INSUFFICIENT_SCOPE: "Dieser Schlüssel wurde ohne Schreibzugriff erstellt.",
    API_ACCESS_DENIED:
      "Die API ist für dieses Konto deaktiviert, oder Sie haben noch nicht aufgeladen.",
    API_DISABLED: "Die API ist plattformweit vorübergehend deaktiviert. Später erneut versuchen.",
    IDEMPOTENCY_KEY_REQUIRED: "Senden Sie bei jedem Kauf einen Idempotency-Key-Header.",
    IDEMPOTENCY_KEY_REUSED: "Gleicher Schlüssel, anderer Body. Korrigieren Sie Ihren Client.",
    IDEMPOTENCY_IN_PROGRESS: "Der erste Versuch läuft noch. In einer Sekunde erneut versuchen.",
    PRICE_ABOVE_MAX: "Der Preis liegt über Ihrem max_price. Neu abfragen und entscheiden.",
    INSUFFICIENT_BALANCE: "Guthaben aufladen. Vorher nicht wiederholen.",
    COUNTRY_OUT_OF_STOCK: "Derzeit keine Nummern. Erneut versuchen oder anderes Land wählen.",
    PROVIDER_CAPACITY: "Dieses Land ist vorübergehend nicht lieferbar. Später erneut versuchen.",
    CONCURRENCY_LIMIT: "Zu viele offene Aktivierungen. Einige abschließen oder stornieren.",
    SPEND_LIMIT: "Stündliches Ausgabelimit erreicht. Nach dem Fenster erneut versuchen.",
    RATE_LIMITED: "Langsamer. Retry-After beachten.",
    VALIDATION_ERROR: "Ihr Request-Body ist falsch. Nicht unverändert wiederholen.",
  },

  lifecycleTitle: "Lebenszyklus einer Aktivierung",
  lifecycleBody: [
    "Eine Aktivierung ist pending, bis ein Code eintrifft (received), Sie sie als benutzt markieren (finished) oder sie storniert wird. Stornieren erstattet genau den berechneten Betrag.",
    "Eine offene Aktivierung verfällt 20 Minuten nach dem Kauf. Wir stornieren sie im Netz und erstatten Ihnen den vollen Betrag, sobald diese Stornierung bestätigt ist — Sie zahlen nie für eine Nummer, die nichts empfangen hat.",
    "Vor dem Stornieren einer Nummer gilt eine Karenzzeit von etwa 120 Sekunden; früheres Stornieren liefert EARLY_CANCEL_DENIED mit den verbleibenden Sekunden.",
  ],

  limitsTitle: "Limits",
  limitsBody:
    "Lesende Anfragen, schreibende Anfragen und die Gesamtzahl pro Nutzer haben je eine Obergrenze pro Minute, dazu optionale Limits für offene Aktivierungen, stündliche Ausgaben und Aktivierungen pro Stunde. Ihre aktuellen Werte stehen in GET /api/v1/me, wobei null „kein Limit“ bedeutet. Für eine Anhebung öffnen Sie ein Ticket — es sind Kontoeinstellungen, keine fest codierten Werte.",

  referenceTitle: "Vollständige Endpunkt-Referenz",
  referenceBody: "Interaktives OpenAPI-3.1-Dokument, stets aus dem laufenden Code generiert.",
  referenceCta: "Referenz öffnen",
};

const it: ApiDocsContent = {
  metaTitle: "API di verifica SMS ed email — SmsEmpire",
  metaDescription:
    "Acquista verifiche SMS ed email dal tuo codice. Webhook firmati, acquisti idempotenti e sconto sul prezzo del sito.",
  heading: "L'API di SmsEmpire",
  subheading:
    "Acquista verifiche SMS ed email dal tuo codice, addebitate sul tuo saldo, con uno sconto sul prezzo del sito.",
  bullets: [
    "Prezzo scontato su ogni acquisto effettuato via API",
    "Webhook firmati appena arriva il codice — nessun ciclo di polling",
    "Acquisti idempotenti: riprovare dopo un timeout è sicuro",
  ],
  ctaKey: "Ottieni la tua chiave API",
  ctaReference: "Riferimento OpenAPI",

  quickstartTitle: "Avvio rapido",
  quickstartBody:
    "Tutto sta sotto https://smsempire.com/api/v1. Autenticati inviando la tua chiave come bearer token. Solo lato server: questa API non invia header CORS, e una chiave usata da un browser è una chiave trapelata.",

  conventionsTitle: "Convenzioni",
  conventions: [
    'Il denaro è una stringa decimale ("0.28000"), mai un numero JSON. Analizzalo con un tipo decimal, non con un float.',
    "Le date sono in RFC3339 UTC.",
    'Gli errori sono sempre {"error": {"code", "message", "request_id"}}. Ramifica su code; message può cambiare.',
    "Cita il request_id in qualsiasi ticket di supporto: punta direttamente a quella richiesta nei nostri log.",
    "I limiti tornano negli header X-RateLimit-*, e un 429 porta Retry-After.",
  ],

  idempotencyTitle: "Idempotenza (obbligatoria negli acquisti)",
  idempotencyBody: [
    "Ogni acquisto richiede un header Idempotency-Key — un UUID è ideale. Riutilizzarlo restituisce la risposta originale con Idempotent-Replay: true invece di comprare un secondo numero.",
    "Esiste per una sola situazione: il tuo POST va in timeout e non sai se è andato a buon fine. Senza la chiave, riprovare compra due volte e non riprovare perde il numero già pagato. Con essa, riprova e basta.",
    "Riusare una chiave con un corpo diverso dà 422, non una ripetizione silenziosa: quella combinazione è un bug lato tuo, e nasconderlo ti darebbe il risultato sbagliato.",
  ],

  maxPriceTitle: "max_price",
  maxPriceBody:
    "Opzionale ma fortemente consigliato. Se ometti il paese ne proviamo diversi, in ordine di qualità e a prezzi diversi; max_price viene verificato su ogni candidato, quindi non ti si può mai addebitare più di quanto hai accettato. Se nessuno rientra, ricevi 409 PRICE_ABOVE_MAX con il prezzo attuale e non viene addebitato nulla.",

  webhooksTitle: "Webhook",
  webhooksBody: [
    "Registra un endpoint HTTPS nella tua dashboard. Inviamo un POST appena arriva un codice. La consegna è almeno una volta, con ritentativi scaglionati per circa due ore e mezza.",
    "Requisiti del tuo endpoint: HTTPS sulla porta 443, che risolva a un indirizzo pubblico. Non seguiamo i redirect e rifiutiamo destinazioni private, di loopback e di metadati cloud.",
  ],

  verifyTitle: "Verificare un webhook",
  verifyIntro:
    'La firma è HMAC-SHA256(segreto, "<timestamp>.<corpo grezzo>"), codificata in esadecimale. Quattro errori comuni, in ordine di frequenza:',
  verifyMistakes: [
    "Firmare il JSON riserializzato invece dei byte grezzi. Riserializzare cambia i byte e la firma non combacerà mai.",
    "Ignorare il timestamp. Senza il controllo ±300 s, una consegna intercettata può esserti riproposta all'infinito.",
    "Confrontare con == invece che con un confronto a tempo costante.",
    "Non deduplicare su X-SmsEmpire-Delivery. Garantiamo almeno una volta, non esattamente una.",
  ],
  verifyRotation:
    "Ruotando il segreto, il precedente resta valido per 24 ore ed entrambi firmano ogni consegna in quella finestra — così puoi rilasciare senza interruzioni.",

  errorsTitle: "Errori",
  errorsColCode: "code",
  errorsColWhat: "Cosa fare",
  errors: {
    INVALID_API_KEY: "Chiave mancante, malformata o sconosciuta. Non riprovare.",
    "KEY_REVOKED / KEY_EXPIRED": "Crea una nuova chiave. Non riprovare.",
    INSUFFICIENT_SCOPE: "Questa chiave è stata creata senza accesso in scrittura.",
    API_ACCESS_DENIED: "L'API è disattivata per questo account, o non hai ancora ricaricato.",
    API_DISABLED: "L'API è temporaneamente disattivata per tutti. Riprova più tardi.",
    IDEMPOTENCY_KEY_REQUIRED: "Invia un header Idempotency-Key a ogni acquisto.",
    IDEMPOTENCY_KEY_REUSED: "Stessa chiave, corpo diverso. Correggi il tuo client.",
    IDEMPOTENCY_IN_PROGRESS: "Il primo tentativo è ancora in corso. Riprova tra un secondo.",
    PRICE_ABOVE_MAX:
      "Il prezzo ha superato il tuo max_price. Richiedi un nuovo preventivo e decidi.",
    INSUFFICIENT_BALANCE: "Ricarica il saldo. Non riprovare prima di averlo fatto.",
    COUNTRY_OUT_OF_STOCK: "Al momento non ci sono numeri. Riprova o scegli un altro paese.",
    PROVIDER_CAPACITY: "Non possiamo fornire quel paese al momento. Riprova più tardi.",
    CONCURRENCY_LIMIT: "Troppe attivazioni in sospeso. Concludine o annullane alcune.",
    SPEND_LIMIT: "Raggiunto il tetto di spesa orario. Riprova dopo la finestra.",
    RATE_LIMITED: "Rallenta. Rispetta Retry-After.",
    VALIDATION_ERROR: "Il corpo della richiesta è errato. Non riprovare senza modificarlo.",
  },

  lifecycleTitle: "Ciclo di vita di un'attivazione",
  lifecycleBody: [
    "Un'attivazione è pending finché non arriva un codice (received), la marchi come usata (finished) o viene annullata. Annullare rimborsa esattamente quanto ti è stato addebitato.",
    "Un'attivazione in sospeso scade 20 minuti dopo l'acquisto. La annulliamo sulla rete e ti rimborsiamo per intero non appena l'annullamento è confermato: non resti mai a pagare un numero che non ha ricevuto nulla.",
    "C'è un periodo di grazia di circa 120 secondi prima di poter annullare un numero; annullare prima restituisce EARLY_CANCEL_DENIED con i secondi da attendere.",
  ],

  limitsTitle: "Limiti",
  limitsBody:
    "Richieste in lettura, in scrittura e totale per utente hanno ciascuna un tetto al minuto, più tetti opzionali su attivazioni in sospeso, spesa oraria e attivazioni orarie. I tuoi valori attuali sono in GET /api/v1/me, dove null significa nessun limite. Se ti servono più alti apri un ticket: sono impostazioni per account, non valori fissi nel codice.",

  referenceTitle: "Riferimento completo degli endpoint",
  referenceBody: "Documento OpenAPI 3.1 interattivo, sempre generato dal codice in esecuzione.",
  referenceCta: "Apri il riferimento",
};

const pt: ApiDocsContent = {
  metaTitle: "API de verificação por SMS e email — SmsEmpire",
  metaDescription:
    "Compre verificações por SMS e email a partir do seu código. Webhooks assinados, compras idempotentes e desconto sobre o preço do site.",
  heading: "A API da SmsEmpire",
  subheading:
    "Compre verificações por SMS e email a partir do seu próprio código, debitadas do seu saldo, com desconto sobre o preço do site.",
  bullets: [
    "Preço com desconto em cada compra feita pela API",
    "Webhooks assinados assim que o código chega — sem ciclo de polling",
    "Compras idempotentes: repetir após um timeout é seguro",
  ],
  ctaKey: "Obter a sua chave de API",
  ctaReference: "Referência OpenAPI",

  quickstartTitle: "Início rápido",
  quickstartBody:
    "Tudo fica sob https://smsempire.com/api/v1. Autentique-se enviando a sua chave como bearer token. Apenas do lado do servidor: esta API não envia cabeçalhos CORS, e uma chave usada num navegador é uma chave exposta.",

  conventionsTitle: "Convenções",
  conventions: [
    'O dinheiro é uma string decimal ("0.28000"), nunca um número JSON. Analise-o com um tipo decimal, não com um float.',
    "As datas são RFC3339 em UTC.",
    'Os erros são sempre {"error": {"code", "message", "request_id"}}. Decida pelo code; message pode mudar.',
    "Cite o request_id em qualquer ticket de suporte: aponta diretamente para esse pedido nos nossos registos.",
    "Os limites voltam nos cabeçalhos X-RateLimit-*, e um 429 traz Retry-After.",
  ],

  idempotencyTitle: "Idempotência (obrigatória nas compras)",
  idempotencyBody: [
    "Cada compra precisa de um cabeçalho Idempotency-Key — um UUID é ideal. Reutilizá-lo devolve a resposta original com Idempotent-Replay: true em vez de comprar um segundo número.",
    "Existe por uma única situação: o seu POST expira e não sabe se chegou a passar. Sem a chave, repetir compra duas vezes e não repetir perde o número já pago. Com ela, basta repetir.",
    "Reutilizar uma chave com um corpo diferente dá 422, não uma repetição silenciosa: essa combinação é um bug do seu lado, e escondê-lo dar-lhe-ia o resultado errado.",
  ],

  maxPriceTitle: "max_price",
  maxPriceBody:
    "Opcional mas muito recomendado. Se omitir o país tentamos vários, por ordem de qualidade e a preços diferentes; max_price é verificado contra cada candidato, por isso nunca lhe podem cobrar mais do que aceitou. Se nenhum servir, recebe 409 PRICE_ABOVE_MAX com o preço atual e nada é cobrado.",

  webhooksTitle: "Webhooks",
  webhooksBody: [
    "Registe um endpoint HTTPS no seu painel. Enviamos um POST assim que um código chega. A entrega é pelo menos uma vez, com repetições escalonadas ao longo de cerca de duas horas e meia.",
    "Requisitos do seu endpoint: HTTPS na porta 443, resolvendo para um endereço público. Não seguimos redirecionamentos e recusamos destinos privados, de loopback e de metadados de nuvem.",
  ],

  verifyTitle: "Verificar um webhook",
  verifyIntro:
    'A assinatura é HMAC-SHA256(segredo, "<timestamp>.<corpo bruto>"), em hexadecimal. Quatro erros frequentes, por ordem:',
  verifyMistakes: [
    "Assinar o JSON re-serializado em vez dos bytes brutos. Re-serializar muda os bytes e a assinatura nunca vai bater certo.",
    "Ignorar o timestamp. Sem a verificação de ±300 s, uma entrega capturada pode ser-lhe reenviada para sempre.",
    "Comparar com == em vez de uma comparação em tempo constante.",
    "Não deduplicar por X-SmsEmpire-Delivery. Garantimos pelo menos uma vez, não exatamente uma.",
  ],
  verifyRotation:
    "Ao rodar o segredo, o anterior continua válido 24 horas e ambos assinam cada entrega nessa janela — pode fazer deploy sem interrupções.",

  errorsTitle: "Erros",
  errorsColCode: "code",
  errorsColWhat: "O que fazer",
  errors: {
    INVALID_API_KEY: "Chave ausente, malformada ou desconhecida. Não repita.",
    "KEY_REVOKED / KEY_EXPIRED": "Crie uma chave nova. Não repita.",
    INSUFFICIENT_SCOPE: "Esta chave foi criada sem acesso de escrita.",
    API_ACCESS_DENIED: "A API está desativada para esta conta, ou ainda não carregou saldo.",
    API_DISABLED: "A API está temporariamente desativada para todos. Tente mais tarde.",
    IDEMPOTENCY_KEY_REQUIRED: "Envie um cabeçalho Idempotency-Key em cada compra.",
    IDEMPOTENCY_KEY_REUSED: "Mesma chave, corpo diferente. Corrija o seu cliente.",
    IDEMPOTENCY_IN_PROGRESS:
      "A primeira tentativa ainda está a decorrer. Repita dentro de um segundo.",
    PRICE_ABOVE_MAX: "O preço subiu acima do seu max_price. Volte a consultar e decida.",
    INSUFFICIENT_BALANCE: "Carregue saldo. Não repita antes disso.",
    COUNTRY_OUT_OF_STOCK: "Sem números neste momento. Repita ou escolha outro país.",
    PROVIDER_CAPACITY: "Não conseguimos fornecer esse país temporariamente. Tente mais tarde.",
    CONCURRENCY_LIMIT: "Demasiadas ativações pendentes. Termine ou cancele algumas.",
    SPEND_LIMIT: "Limite de gasto por hora atingido. Tente após a janela.",
    RATE_LIMITED: "Abrande. Respeite o Retry-After.",
    VALIDATION_ERROR: "O corpo do seu pedido está errado. Não repita sem o alterar.",
  },

  lifecycleTitle: "Ciclo de vida de uma ativação",
  lifecycleBody: [
    "Uma ativação está pending até chegar um código (received), a marcar como usada (finished) ou ser cancelada. Cancelar devolve exatamente o que foi cobrado.",
    "Uma ativação pendente expira 20 minutos após a compra. Cancelamo-la na rede e devolvemos o valor integral assim que esse cancelamento é confirmado — nunca fica a pagar por um número que não recebeu nada.",
    "Há um período de graça de cerca de 120 segundos antes de poder cancelar um número; cancelar antes devolve EARLY_CANCEL_DENIED com os segundos em falta.",
  ],

  limitsTitle: "Limites",
  limitsBody:
    "Pedidos de leitura, de escrita e o total por utilizador têm cada um um teto por minuto, além de tetos opcionais para ativações pendentes, gasto por hora e ativações por hora. Os seus valores atuais estão em GET /api/v1/me, onde null significa sem limite. Se precisar de os aumentar, abra um ticket: são definições por conta, não valores fixos no código.",

  referenceTitle: "Referência completa dos endpoints",
  referenceBody: "Documento OpenAPI 3.1 interativo, sempre gerado a partir do código em execução.",
  referenceCta: "Abrir a referência",
};

const zh: ApiDocsContent = {
  metaTitle: "短信与邮箱验证 API — SmsEmpire",
  metaDescription:
    "在你自己的代码中购买短信和邮箱验证。带签名的 Webhook、幂等下单，并享受低于网站价格的折扣。",
  heading: "SmsEmpire API",
  subheading: "在你自己的代码中购买短信和邮箱验证，从账户余额扣费，并享受低于网站价格的折扣。",
  bullets: [
    "通过 API 的每一笔购买都享受折扣价",
    "验证码到达即发送带签名的 Webhook —— 无需轮询",
    "幂等下单：网络超时后重试是安全的",
  ],
  ctaKey: "获取 API 密钥",
  ctaReference: "OpenAPI 参考",

  quickstartTitle: "快速开始",
  quickstartBody:
    "所有接口都在 https://smsempire.com/api/v1 之下。把密钥作为 bearer token 发送即可完成认证。仅限服务端使用：本 API 不发送任何 CORS 响应头，在浏览器中使用的密钥就是已经泄露的密钥。",

  conventionsTitle: "约定",
  conventions: [
    '金额是十进制字符串（"0.28000"），绝不是 JSON 数字。请用 decimal 类型解析，不要用 float。',
    "时间戳采用 RFC3339 UTC 格式。",
    '错误格式始终是 {"error": {"code", "message", "request_id"}}。请依据 code 分支处理；message 可能变化。',
    "在任何工单中附上 request_id：它能直接定位到我们日志中的这次请求。",
    "限流信息通过 X-RateLimit-* 响应头返回，429 会带上 Retry-After。",
  ],

  idempotencyTitle: "幂等性（下单必需）",
  idempotencyBody: [
    "每次购买都必须带 Idempotency-Key 请求头，建议用 UUID。重复使用同一个键会返回原始响应并带上 Idempotent-Replay: true，而不是再买一个号码。",
    "它只为一种情况而存在：你的 POST 超时了，而你不知道它是否已经生效。没有这个键，重试会买两次，不重试则会丢掉已经付过钱的号码。有了它，直接重试即可。",
    "同一个键配上不同的请求体会返回 422，而不是静默重放——这种组合是你这边的 bug，掩盖它只会给你错误的结果。",
  ],

  maxPriceTitle: "max_price",
  maxPriceBody:
    "可选，但强烈建议使用。如果你不指定国家，我们会按质量顺序尝试多个、价格各不相同的国家；max_price 会对每一个候选逐一校验，因此绝不会向你收取超过你认可的金额。如果没有符合的，会返回 409 PRICE_ABOVE_MAX 并附上当前价格，且不产生任何扣费。",

  webhooksTitle: "Webhook",
  webhooksBody: [
    "在控制台注册一个 HTTPS 端点。验证码一到达，我们就会向它发送 POST。投递保证至少一次，并在约两个半小时内按退避策略重试。",
    "对你的端点的要求：HTTPS、443 端口，且解析到公网地址。我们不跟随重定向，并拒绝私有地址、回环地址和云元数据地址。",
  ],

  verifyTitle: "校验 Webhook",
  verifyIntro:
    '签名为 HMAC-SHA256(secret, "<timestamp>.<原始报文>") 的十六进制编码。按常见程度排列的四个易犯错误：',
  verifyMistakes: [
    "对重新序列化后的 JSON 签名，而不是原始字节。重新序列化会改变字节，签名永远对不上。",
    "忽略时间戳。没有 ±300 秒的校验，被截获的一次投递可以被永久重放给你。",
    "用 == 比较，而不是恒定时间比较。",
    "没有按 X-SmsEmpire-Delivery 去重。我们保证至少一次，而不是恰好一次。",
  ],
  verifyRotation:
    "轮换密钥后，旧密钥仍有效 24 小时，且该窗口内每次投递都用两个密钥同时签名——因此你可以无中断地完成部署。",

  errorsTitle: "错误",
  errorsColCode: "code",
  errorsColWhat: "应对方式",
  errors: {
    INVALID_API_KEY: "密钥缺失、格式错误或不存在。不要重试。",
    "KEY_REVOKED / KEY_EXPIRED": "请创建新密钥。不要重试。",
    INSUFFICIENT_SCOPE: "该密钥创建时没有写权限。",
    API_ACCESS_DENIED: "本账户的 API 已关闭，或你尚未充值。",
    API_DISABLED: "全平台暂时关闭了 API。请稍后重试。",
    IDEMPOTENCY_KEY_REQUIRED: "每次购买都要发送 Idempotency-Key 请求头。",
    IDEMPOTENCY_KEY_REUSED: "同一个键配了不同的请求体。请修正你的客户端。",
    IDEMPOTENCY_IN_PROGRESS: "第一次尝试仍在处理中。请一秒后重试。",
    PRICE_ABOVE_MAX: "价格已超过你的 max_price。请重新询价再决定。",
    INSUFFICIENT_BALANCE: "请充值。充值前不要重试。",
    COUNTRY_OUT_OF_STOCK: "当前没有号码。请重试或换一个国家。",
    PROVIDER_CAPACITY: "该国家暂时无法供货。请稍后重试。",
    CONCURRENCY_LIMIT: "待处理的激活过多。请先完成或取消一些。",
    SPEND_LIMIT: "已达每小时消费上限。请等本窗口结束后重试。",
    RATE_LIMITED: "请放慢速度，并遵守 Retry-After。",
    VALIDATION_ERROR: "请求体有误。不要原样重试。",
  },

  lifecycleTitle: "激活的生命周期",
  lifecycleBody: [
    "激活在收到验证码前处于 pending；收到后变为 received，你标记为已用则是 finished，也可以被取消。取消会原额退回你被扣除的金额。",
    "待处理的激活在购买 20 分钟后过期。我们会向网络发起取消，并在该取消被确认后向你全额退款——你绝不会为一个什么都没收到的号码买单。",
    "号码在购买后约 120 秒内不能取消；过早取消会返回 EARLY_CANCEL_DENIED，并附上还需等待的秒数。",
  ],

  limitsTitle: "限制",
  limitsBody:
    "读请求、写请求和每用户总请求各有每分钟上限，另有可选的待处理激活数、每小时消费额和每小时激活数上限。你当前的数值可在 GET /api/v1/me 查看，其中 null 表示不限。需要调高请开工单：这些是按账户配置的，并非写死在代码里。",

  referenceTitle: "完整接口参考",
  referenceBody: "交互式 OpenAPI 3.1 文档，始终由运行中的代码生成。",
  referenceCta: "打开参考文档",
};

const ru: ApiDocsContent = {
  metaTitle: "API для SMS- и email-верификации — SmsEmpire",
  metaDescription:
    "Покупайте SMS- и email-верификации из своего кода. Подписанные вебхуки, идемпотентные покупки и скидка от цены сайта.",
  heading: "API SmsEmpire",
  subheading:
    "Покупайте SMS- и email-верификации из своего кода, списывая с баланса, со скидкой от цены сайта.",
  bullets: [
    "Скидка на каждую покупку через API",
    "Подписанные вебхуки сразу при получении кода — без опроса",
    "Идемпотентные покупки: повтор после таймаута безопасен",
  ],
  ctaKey: "Получить ключ API",
  ctaReference: "Справочник OpenAPI",

  quickstartTitle: "Быстрый старт",
  quickstartBody:
    "Всё находится по адресу https://smsempire.com/api/v1. Аутентификация — ключ в виде bearer-токена. Только со стороны сервера: API не отправляет CORS-заголовки, а ключ, использованный в браузере, — это утёкший ключ.",

  conventionsTitle: "Соглашения",
  conventions: [
    'Деньги — десятичная строка ("0.28000"), никогда не число JSON. Разбирайте её типом decimal, а не float.',
    "Время — RFC3339 в UTC.",
    'Ошибки всегда имеют вид {"error": {"code", "message", "request_id"}}. Ветвитесь по code; message может измениться.',
    "Указывайте request_id в обращении в поддержку: он ведёт прямо к этому запросу в наших логах.",
    "Лимиты возвращаются в заголовках X-RateLimit-*, а 429 несёт Retry-After.",
  ],

  idempotencyTitle: "Идемпотентность (обязательна при покупке)",
  idempotencyBody: [
    "Каждая покупка требует заголовок Idempotency-Key — лучше всего UUID. Повторное использование вернёт исходный ответ с Idempotent-Replay: true, а не купит второй номер.",
    "Это нужно ровно для одной ситуации: ваш POST завершился таймаутом, и вы не знаете, прошёл ли он. Без ключа повтор купит дважды, а отказ от повтора потеряет уже оплаченный номер. С ключом просто повторяйте.",
    "Тот же ключ с другим телом даёт 422, а не тихий повтор: такое сочетание — ошибка на вашей стороне, и скрыть её значило бы выдать вам неверный результат.",
  ],

  maxPriceTitle: "max_price",
  maxPriceBody:
    "Необязательный, но настоятельно рекомендуемый параметр. Если страна не указана, мы пробуем несколько — по порядку качества и с разными ценами; max_price проверяется для каждого кандидата, поэтому с вас никогда не спишут больше согласованного. Если не подходит ничего, вы получите 409 PRICE_ABOVE_MAX с текущей ценой, и списания не будет.",

  webhooksTitle: "Вебхуки",
  webhooksBody: [
    "Зарегистрируйте один HTTPS-эндпоинт в панели. Мы отправляем POST сразу при получении кода. Доставка «хотя бы один раз», с повторами по нарастающей примерно два с половиной часа.",
    "Требования к эндпоинту: HTTPS на порту 443, резолвящийся в публичный адрес. Мы не следуем редиректам и отклоняем приватные, loopback- и облачные metadata-адреса.",
  ],

  verifyTitle: "Проверка вебхука",
  verifyIntro:
    'Подпись — HMAC-SHA256(секрет, "<timestamp>.<сырое тело>") в hex. Четыре типичные ошибки, по частоте:',
  verifyMistakes: [
    "Подписывать пересериализованный JSON вместо сырых байтов. Пересериализация меняет байты, и подпись никогда не сойдётся.",
    "Игнорировать метку времени. Без проверки ±300 с перехваченную доставку можно повторять вам бесконечно.",
    "Сравнивать через == вместо сравнения за постоянное время.",
    "Не дедуплицировать по X-SmsEmpire-Delivery. Мы гарантируем «хотя бы один раз», а не «ровно один раз».",
  ],
  verifyRotation:
    "При ротации прежний секрет остаётся действительным 24 часа, и в этом окне каждую доставку подписывают оба — так что выкатывать можно без простоя.",

  errorsTitle: "Ошибки",
  errorsColCode: "code",
  errorsColWhat: "Что делать",
  errors: {
    INVALID_API_KEY: "Ключ отсутствует, повреждён или неизвестен. Не повторяйте.",
    "KEY_REVOKED / KEY_EXPIRED": "Создайте новый ключ. Не повторяйте.",
    INSUFFICIENT_SCOPE: "Этот ключ создан без права записи.",
    API_ACCESS_DENIED: "API отключён для этого аккаунта, либо вы ещё не пополняли баланс.",
    API_DISABLED: "API временно отключён для всех. Повторите позже.",
    IDEMPOTENCY_KEY_REQUIRED: "Отправляйте заголовок Idempotency-Key при каждой покупке.",
    IDEMPOTENCY_KEY_REUSED: "Тот же ключ, другое тело. Исправьте клиент.",
    IDEMPOTENCY_IN_PROGRESS: "Первая попытка ещё выполняется. Повторите через секунду.",
    PRICE_ABOVE_MAX: "Цена превысила ваш max_price. Запросите цену заново и решите.",
    INSUFFICIENT_BALANCE: "Пополните баланс. До этого не повторяйте.",
    COUNTRY_OUT_OF_STOCK: "Сейчас номеров нет. Повторите или выберите другую страну.",
    PROVIDER_CAPACITY: "Временно не можем выдать эту страну. Повторите позже.",
    CONCURRENCY_LIMIT: "Слишком много активаций в ожидании. Завершите или отмените часть.",
    SPEND_LIMIT: "Достигнут часовой лимит трат. Повторите после окончания окна.",
    RATE_LIMITED: "Снизьте темп. Соблюдайте Retry-After.",
    VALIDATION_ERROR: "Тело запроса неверно. Не повторяйте без изменений.",
  },

  lifecycleTitle: "Жизненный цикл активации",
  lifecycleBody: [
    "Активация находится в pending, пока не придёт код (received), пока вы не отметите её использованной (finished) или пока её не отменят. Отмена возвращает ровно ту сумму, что была списана.",
    "Ожидающая активация истекает через 20 минут после покупки. Мы отменяем её в сети и возвращаем вам полную сумму, как только отмена подтверждена, — вы никогда не платите за номер, на который ничего не пришло.",
    "Есть льготный период около 120 секунд, прежде чем номер можно отменить; более ранняя отмена вернёт EARLY_CANCEL_DENIED с числом секунд ожидания.",
  ],

  limitsTitle: "Лимиты",
  limitsBody:
    "У запросов на чтение, на запись и у общего числа запросов пользователя есть свой потолок в минуту, плюс необязательные лимиты на число активаций в ожидании, часовые траты и число активаций в час. Ваши текущие значения — в GET /api/v1/me, где null означает «без лимита». Нужно поднять — напишите в поддержку: это настройки аккаунта, а не константы в коде.",

  referenceTitle: "Полный справочник эндпоинтов",
  referenceBody: "Интерактивный документ OpenAPI 3.1, всегда генерируемый из работающего кода.",
  referenceCta: "Открыть справочник",
};

const CONTENT: Record<string, ApiDocsContent> = { en, es, fr, de, it, pt, zh, ru };

export function apiDocsContent(lang: string): ApiDocsContent {
  return CONTENT[lang] ?? en;
}
